import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { extrairMensagemErro } from '~/Utils/Helpers';
import { listarContasIsencao, verificarNegativacaoConta, incluirIsencaoCobranca } from '~/Services/Service';
import { schemaIncluirIsencao } from './schemas';
import ConfirmReabilitarNegativacao from './ConfirmReabilitarNegativacao';

const amanha = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const ModalIncluirIsencao = ({ show, geOptions, onHide, onSucesso }) => {
  const [contasOptions, setContasOptions] = useState([]);
  const [loadingContas, setLoadingContas] = useState(false);

  // confirm CAF 2957
  const [showConfirmNeg, setShowConfirmNeg] = useState(false);
  const [parcelasNegativadas, setParcelasNegativadas] = useState([]);
  const [payloadPendente, setPayloadPendente] = useState(null);

  const handleGeChange = (ge, setFieldValue) => {
    setFieldValue('codigo_grupo_empresa', ge ? ge.value : null);
    setFieldValue('codigo_conta', null);
    setContasOptions([]);
    if (!ge) return;

    // flag anti-race (LICOES ciclo 9 L3)
    setLoadingContas(true);
    let ativo = true;
    listarContasIsencao(ge.value)
      .then((res) => {
        if (!ativo) return;
        const lista = res || [];
        setContasOptions(
          lista.map((c) => ({
            value: c.codigo_conta,
            label: `${c.codigo_conta} - ${c.apelido_conta || ''}`.trim(),
          }))
        );
      })
      .catch(() => {
        if (!ativo) return;
        setContasOptions([]);
      })
      .finally(() => {
        if (!ativo) return;
        setLoadingContas(false);
      });
    return () => {
      ativo = false;
    };
  };

  // submit com fluxo CAF 2957
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const resp = await verificarNegativacaoConta(values.codigo_conta, values.codigo_grupo_empresa);

      if (resp && resp.scpc_em_andamento) {
        toast.error('Transmissão SCPC em andamento. Tente novamente após a finalização da transmissão.');
        setSubmitting(false);
        return;
      }

      const payload = {
        codigo_grupo_empresa: values.codigo_grupo_empresa,
        codigo_conta: values.codigo_conta,
        motivo: values.motivo.trim(),
        validade: values.validade,
        excluir_negativacao: false,
      };

      if (resp && resp.tem_negativacao) {
        setParcelasNegativadas(resp.parcelas || []);
        setPayloadPendente({ payload, setSubmitting });
        setShowConfirmNeg(true);
      } else {
        await incluirIsencaoCobranca(payload);
        toast.success('Cliente incluído na isenção de cobrança');
        onSucesso();
      }
    } catch (err) {
      toast.error(extrairMensagemErro(err));
      setSubmitting(false);
    }
  };

  const handleConfirmNeg = async () => {
    if (!payloadPendente) return;
    const { payload, setSubmitting } = payloadPendente;
    setShowConfirmNeg(false);
    try {
      await incluirIsencaoCobranca({ ...payload, excluir_negativacao: true });
      toast.success('Cliente incluído na isenção de cobrança');
      onSucesso();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
      setSubmitting(false);
    }
    setPayloadPendente(null);
  };

  const handleRejeitarNeg = async () => {
    if (!payloadPendente) return;
    const { payload, setSubmitting } = payloadPendente;
    setShowConfirmNeg(false);
    try {
      await incluirIsencaoCobranca({ ...payload, excluir_negativacao: false });
      toast.success('Cliente incluído na isenção de cobrança');
      onSucesso();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
      setSubmitting(false);
    }
    setPayloadPendente(null);
  };

  return (
    <>
      <Modal show={show && !showConfirmNeg} onHide={onHide} centered>
        <Formik
          initialValues={{
            codigo_grupo_empresa: null,
            codigo_conta: null,
            motivo: '',
            validade: '',
          }}
          validationSchema={schemaIncluirIsencao}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit: fSubmit, setFieldValue, isSubmitting }) => {
            const geValue = geOptions.find((o) => o.value === values.codigo_grupo_empresa) || null;
            const contaValue = contasOptions.find((o) => o.value === values.codigo_conta) || null;

            return (
              <Form noValidate onSubmit={fSubmit}>
                <Modal.Header closeButton>
                  <Modal.Title>Nova Isenção de Cobrança</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Row>
                    <Col xs={12} className="mb-3">
                      <Form.Label className="small mb-1">Empreendimento (GE) *</Form.Label>
                      <Select
                        options={geOptions}
                        value={geValue}
                        onChange={(opt) => handleGeChange(opt, setFieldValue)}
                        isClearable
                        placeholder="Selecione..."
                        styles={selectStylesSm}
                      />
                      {touched.codigo_grupo_empresa && errors.codigo_grupo_empresa && (
                        <div className="text-danger small mt-1">{errors.codigo_grupo_empresa}</div>
                      )}
                    </Col>
                    <Col xs={12} className="mb-3">
                      <Form.Label className="small mb-1">Conta *</Form.Label>
                      <Select
                        options={contasOptions}
                        value={contaValue}
                        onChange={(opt) => setFieldValue('codigo_conta', opt ? opt.value : null)}
                        isClearable
                        placeholder={values.codigo_grupo_empresa ? 'Selecione...' : 'Selecione um GE'}
                        styles={selectStylesSm}
                        isDisabled={!values.codigo_grupo_empresa || loadingContas}
                        isLoading={loadingContas}
                      />
                      {touched.codigo_conta && errors.codigo_conta && (
                        <div className="text-danger small mt-1">{errors.codigo_conta}</div>
                      )}
                    </Col>
                    <Col xs={12} className="mb-3">
                      <Form.Label className="small mb-1">Motivo *</Form.Label>
                      <Form.Control
                        type="text"
                        name="motivo"
                        size="sm"
                        value={values.motivo}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.motivo && !!errors.motivo}
                        maxLength={200}
                        placeholder="Informe o motivo"
                      />
                      <Form.Control.Feedback type="invalid">{errors.motivo}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12} className="mb-3">
                      <Form.Label className="small mb-1">Validade *</Form.Label>
                      <Form.Control
                        type="date"
                        name="validade"
                        size="sm"
                        value={values.validade}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.validade && !!errors.validade}
                        min={amanha()}
                      />
                      <Form.Control.Feedback type="invalid">{errors.validade}</Form.Control.Feedback>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" size="sm" onClick={onHide} disabled={isSubmitting}>
                    Cancelar
                  </Button>
                  <Button variant="primary" size="sm" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Aguarde...' : 'Incluir'}
                  </Button>
                </Modal.Footer>
              </Form>
            );
          }}
        </Formik>
      </Modal>

      {/* Confirm aninhado CAF 2957 */}
      <ConfirmReabilitarNegativacao
        show={showConfirmNeg}
        parcelas={parcelasNegativadas}
        onConfirm={handleConfirmNeg}
        onRejeitar={handleRejeitarNeg}
      />
    </>
  );
};

export default ModalIncluirIsencao;
